import { Box, useMantineTheme } from '@mantine/core';
import { useWindowScroll } from '@mantine/hooks';
import { forwardRef, useMemo } from 'react';

export interface ScrollProgressProps {
  position?: 'top' | 'bottom';
  height?: number;
  color?: string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  zIndex?: number;
  showPercentage?: boolean;
}

const ScrollProgress = forwardRef<HTMLDivElement, ScrollProgressProps>(
  (
    {
      position = 'top',
      height = 4,
      color,
      gradientVariant = 'primary',
      zIndex = 1000,
      showPercentage = false,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const [{ y: scrollY }] = useWindowScroll();

    const progress = useMemo(() => {
      if (typeof document === 'undefined') return 0;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      return docHeight > 0 ? (scrollY / docHeight) * 100 : 0;
    }, [scrollY]);

    const backgroundColor = color || (gradient
      ? `linear-gradient(90deg, ${gradient.from}, ${gradient.to})`
      : theme.primaryColor);

    return (
      <Box
        ref={ref}
        pos="fixed"
        left={0}
        right={0}
        style={{
          [position]: 0,
          zIndex,
          height,
          backgroundColor: theme.colors.gray[2],
        }}
      >
        <Box
          h="100%"
          style={{
            width: `${progress}%`,
            background: backgroundColor,
            transition: 'width 0.1s linear',
          }}
        />
        {showPercentage && (
          <Box
            pos="absolute"
            right={8}
            top="50%"
            style={{
              transform: 'translateY(-50%)',
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            {Math.round(progress)}%
          </Box>
        )}
      </Box>
    );
  }
);

ScrollProgress.displayName = 'ScrollProgress';

export default ScrollProgress;
